import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/api/errors';
import {
  fetchPoDetail,
  addPoLine,
  patchPoLine,
  deletePoLine,
  type PoEditDetail,
  type PoEditLine,
  type AddPoLinePayload,
} from '@/api/onderaannemersPoEdit';
import { fetchVendorPrices, type VendorPriceRow } from '@/api/onderaannemerPrijzen';
import type { LineSavePatch } from './PoEditLineRow';
import { PoEditLineList } from './PoEditLineList';
import { AddSectionModal } from './AddSectionModal';
import { AddCollectorModal } from './AddCollectorModal';
import { AddMinprijsModal } from './AddMinprijsModal';
import { AddKmModal } from './AddKmModal';
import { AddMeerwerkModal } from './AddMeerwerkModal';
import { tierAvailability } from './tierLogic';

const euro = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });

type AddKind = 'section' | 'collector' | 'minprijs' | 'km' | 'meerwerk' | null;

interface PoEditPanelProps {
  poId: number;
  // Called when the user clicks "Klaar" — the parent refetches its overview.
  onDone: () => void;
}

/** Full edit mode for ONE open rolling PO: DnD reorder + add / edit / delete lines. */
export function PoEditPanel({ poId, onDone }: PoEditPanelProps) {
  const [detail, setDetail] = useState<PoEditDetail | null>(null);
  // The working line order (optimistically mutated by reorder; `detail` stays the base).
  const [lines, setLines] = useState<PoEditLine[]>([]);
  const [vendorPriceRows, setVendorPriceRows] = useState<VendorPriceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [addKind, setAddKind] = useState<AddKind>(null);
  const [pendingDelete, setPendingDelete] = useState<PoEditLine | null>(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const applyDetail = useCallback((d: PoEditDetail) => {
    setDetail(d);
    setLines(d.lines);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchPoDetail(poId);
      applyDetail(d);
      // Best-effort: prices power the add-collector tier UI. A failure there shouldn't
      // block editing — the collector modal falls back to a required typed price.
      try {
        const priceResp = await fetchVendorPrices();
        const vendor = priceResp.vendors.find((v) => v.vendorId === d.vendorId);
        setVendorPriceRows(vendor?.rows ?? []);
      } catch {
        setVendorPriceRows([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PO laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [poId, applyDetail]);

  useEffect(() => {
    load();
  }, [load]);

  // Product ids for which the vendor prices BOTH tiers → inline tier switch is offered.
  const tierSwitchableProductIds = useMemo(() => {
    const set = new Set<number>();
    for (const row of vendorPriceRows) {
      if (tierAvailability(row).showToggle) set.add(row.productId);
    }
    return set;
  }, [vendorPriceRows]);

  const refetchDetail = useCallback(async () => {
    try {
      applyDetail(await fetchPoDetail(poId));
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Vernieuwen mislukt');
    }
  }, [poId, applyDetail, showToast]);

  const handleAdd = useCallback(
    async (payload: AddPoLinePayload) => {
      await addPoLine(poId, payload);
      // The add returns one line, but we want fresh totals + sequences → refetch detail.
      await refetchDetail();
      setAddKind(null);
    },
    [poId, refetchDetail],
  );

  const handleSaveLine = useCallback(
    async (lineId: number, patch: LineSavePatch) => {
      await patchPoLine(poId, lineId, patch);
      await refetchDetail();
    },
    [poId, refetchDetail],
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const isSection = pendingDelete.displayType === 'line_section';
    setDeleting(true);
    try {
      await deletePoLine(poId, pendingDelete.id, isSection);
      setPendingDelete(null);
      await refetchDetail();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, poId, refetchDetail, showToast]);

  const handleReorderFailed = useCallback(
    (message: string) => {
      showToast(message);
      void refetchDetail();
    },
    [showToast, refetchDetail],
  );

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="inline-block w-7 h-7 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
          <span className="sr-only">Laden…</span>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="p-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="text-sm font-semibold">{error ?? 'PO niet gevonden'}</p>
        </div>
        <button type="button" onClick={onDone} className="mt-3 text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-td-navy hover:bg-gray-50">
          Terug
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 p-3 sm:p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-td-navy truncate">{detail.vendorName}</span>
            <span className="text-xs font-mono text-td-muted bg-gray-100 px-1.5 py-0.5 rounded shrink-0">{detail.poName}</span>
          </div>
          <div className="text-xs text-td-muted mt-0.5">Bewerkmodus — sleep om te herordenen</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="font-bold text-td-navy">{euro.format(detail.amountUntaxed)}</div>
            <div className="text-[11px] text-td-muted">excl. btw</div>
          </div>
          <button
            type="button"
            onClick={onDone}
            className="text-sm px-3 py-1.5 rounded-lg bg-td-blue text-white font-semibold hover:bg-td-blue/90"
          >
            Klaar
          </button>
        </div>
      </div>

      {/* Add buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        <AddBtn onClick={() => setAddKind('section')}>+ Sectie</AddBtn>
        <AddBtn onClick={() => setAddKind('collector')}>+ Collector</AddBtn>
        <AddBtn onClick={() => setAddKind('minprijs')}>+ Minprijs</AddBtn>
        <AddBtn onClick={() => setAddKind('km')}>+ Km</AddBtn>
        <AddBtn onClick={() => setAddKind('meerwerk')}>+ Meerwerk</AddBtn>
      </div>

      {/* Line list */}
      {lines.length === 0 ? (
        <p className="text-sm text-td-muted italic px-2 py-4">Deze PO heeft nog geen regels.</p>
      ) : (
        <PoEditLineList
          poId={poId}
          lines={lines}
          tierSwitchableProductIds={tierSwitchableProductIds}
          onOptimisticReorder={setLines}
          onSaveLine={handleSaveLine}
          onDeleteLine={setPendingDelete}
          onReorderFailed={handleReorderFailed}
        />
      )}

      {/* Add modals */}
      {addKind === 'section' && (
        <AddSectionModal onAdd={(p) => handleAdd(p)} onClose={() => setAddKind(null)} />
      )}
      {addKind === 'collector' && (
        <AddCollectorModal
          vendorName={detail.vendorName}
          vendorPriceRows={vendorPriceRows}
          onAdd={(p) => handleAdd(p)}
          onClose={() => setAddKind(null)}
        />
      )}
      {addKind === 'minprijs' && (
        <AddMinprijsModal onAdd={(p) => handleAdd(p)} onClose={() => setAddKind(null)} />
      )}
      {addKind === 'km' && (
        <AddKmModal onAdd={(p) => handleAdd(p)} onClose={() => setAddKind(null)} />
      )}
      {addKind === 'meerwerk' && (
        <AddMeerwerkModal onAdd={(p) => handleAdd(p)} onClose={() => setAddKind(null)} />
      )}

      {/* Delete confirm */}
      {pendingDelete && (
        <DeleteConfirm
          line={pendingDelete}
          deleting={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-2 rounded-lg shadow-lg bg-red-600 text-white text-sm font-semibold" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

function AddBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm font-semibold text-td-blue border border-td-blue/40 rounded-lg px-3 py-1.5 hover:bg-td-blue/5 transition-colors"
    >
      {children}
    </button>
  );
}

function DeleteConfirm({
  line,
  deleting,
  onConfirm,
  onCancel,
}: {
  line: PoEditLine;
  deleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isSection = line.displayType === 'line_section';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="po-line-delete-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 id="po-line-delete-title" className="text-lg font-extrabold text-td-navy">
            {isSection ? 'Sectie + regels verwijderen?' : 'Regel verwijderen?'}
          </h3>
          <p className="text-sm text-td-muted mt-2">
            {isSection
              ? 'De sectie en alle regels eronder (tot de volgende sectie) worden verwijderd. Dit kan niet ongedaan gemaakt worden.'
              : 'Deze regel wordt verwijderd. Dit kan niet ongedaan gemaakt worden.'}
          </p>
          <p className="text-sm font-semibold text-td-navy mt-2 truncate">{line.label}</p>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <button type="button" onClick={onCancel} disabled={deleting}
            className="px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50">
            Annuleren
          </button>
          <button type="button" onClick={onConfirm} disabled={deleting}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
            {deleting ? 'Bezig…' : 'Verwijderen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Re-export so callers can catch the shared ApiError type without a separate import.
export { ApiError };
